import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  signal,
} from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { ToastController } from '@ionic/angular';
import { finalize } from 'rxjs';

import {
  GroupManagementService,
  PastParticipant,
} from '@services/group-management.service';
import { PanelHostService } from '@services/panel-host.service';
import { TripService } from '@services/trip.service';
import { ContentContainerComponent } from '../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../ui/panel-body/panel-body.component';

@Component({
  selector: 'app-past-participants',
  imports: [TranslocoDirective, ContentContainerComponent, PanelBodyComponent],
  templateUrl: './past-participants.component.html',
  styleUrls: ['./past-participants.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PastParticipantsComponent {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripService = inject(TripService);
  private readonly groupManagementService = inject(GroupManagementService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);

  readonly loading = signal(false);
  readonly loadError = signal(false);
  readonly submitting = signal(false);
  readonly pastParticipants = signal<PastParticipant[]>([]);
  readonly selectedIds = signal<Set<number>>(new Set());

  constructor() {
    effect(() => {
      if (this.panelHost.activePanel() === 'past-participants') {
        this.resetState();
        this.loadPastParticipants();
      }
    });
  }

  cancel(): void {
    this.panelHost.dismiss();
  }

  isSelected(id: number): boolean {
    return this.selectedIds().has(id);
  }

  toggleSelection(id: number): void {
    const next = new Set(this.selectedIds());
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    this.selectedIds.set(next);
  }

  selectAll(): void {
    this.selectedIds.set(new Set(this.pastParticipants().map((p) => p.id)));
  }

  selectNone(): void {
    this.selectedIds.set(new Set());
  }

  submit(): void {
    const trip = this.tripService.currentTrip.value();
    const selected = this.pastParticipants().filter((p) => this.selectedIds().has(p.id));
    if (!trip || selected.length === 0 || this.submitting()) {
      return;
    }

    this.submitting.set(true);
    this.groupManagementService
      .addParticipants(
        trip.id,
        selected.map((p) => ({
          firstname: p.firstname,
          lastname: p.lastname,
          email: p.email,
        })),
      )
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(
              marker('group-management.panels.past-participants.success'),
              { count: selected.length },
            ),
            duration: 4000,
            color: 'success',
          });
          await toast.present();
          this.panelHost.dismiss();
        },
        error: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(
              marker('group-management.panels.past-participants.error'),
            ),
            duration: 4000,
            color: 'danger',
          });
          await toast.present();
        },
      });
  }

  private resetState(): void {
    this.loading.set(false);
    this.loadError.set(false);
    this.submitting.set(false);
    this.pastParticipants.set([]);
    this.selectedIds.set(new Set());
  }

  private loadPastParticipants(): void {
    const trip = this.tripService.currentTrip.value();
    if (!trip) {
      return;
    }

    this.loading.set(true);
    this.loadError.set(false);
    this.groupManagementService.getPastParticipants(trip.id).subscribe({
      next: (rows) => {
        this.pastParticipants.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.loadError.set(true);
        this.loading.set(false);
      },
    });
  }
}
